/* ============================================================
   Dip Extension landing page
   Replaces the prototype's React logic. Two jobs only:
   1. carousel arrows + dot state (accordions are native <details>)
   2. Klaviyo Notify Me form trigger
   Idempotent: safe to re-run after a theme editor section reload.
   ============================================================ */
(function () {
  'use strict';

  function initCarousel(root) {
    if (root.dataset.dglReady === '1') return;
    root.dataset.dglReady = '1';

    var track = root.querySelector('[data-dgl-track]');
    var prev = root.querySelector('[data-dgl-prev]');
    var next = root.querySelector('[data-dgl-next]');
    var dotsWrap = root.parentElement.querySelector('[data-dgl-dots]');
    if (!track) return;

    var index = 0;
    var animating = false;

    function cards() {
      return Array.prototype.slice.call(track.children);
    }

    // Scroll offset that puts a given card at the track's snap edge.
    // Uses real element positions, so card width, gap and scroll-padding
    // never have to be guessed at.
    function offsetOf(card) {
      return card.offsetLeft - track.offsetLeft;
    }

    function nearestIndex() {
      var list = cards();
      var pos = track.scrollLeft;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < list.length; i++) {
        var d = Math.abs(offsetOf(list[i]) - pos);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }

    // How many cards fit in view - a "page" for the dots.
    function perPage() {
      var list = cards();
      if (list.length < 2) return 1;
      var step = offsetOf(list[1]) - offsetOf(list[0]);
      if (step <= 0) return 1;
      return Math.max(1, Math.round(track.clientWidth / step));
    }

    function pageCount() {
      return Math.max(1, Math.ceil(cards().length / perPage()));
    }

    function buildDots() {
      if (!dotsWrap) return;
      var total = pageCount();
      if (dotsWrap.children.length === total) return;
      dotsWrap.textContent = '';
      for (var i = 0; i < total; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'dgl-dot';
        b.dataset.dglPage = String(i);
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dotsWrap.appendChild(b);
      }
    }

    function paint() {
      var page = Math.floor(index / perPage());
      if (dotsWrap) {
        Array.prototype.forEach.call(dotsWrap.children, function (dot, i) {
          dot.setAttribute('aria-current', i === page ? 'true' : 'false');
        });
      }
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    }

    // Scroll to a card by its real offset. No smooth-scroll fighting the snap
    // engine: snapping is suspended for the duration of the animation.
    function goTo(i) {
      var list = cards();
      if (!list.length) return;
      i = Math.max(0, Math.min(list.length - 1, i));
      index = i;

      // Lock out the scroll listener for the duration of the animation.
      // Without this it re-derives the index from whatever card is passing
      // by mid-flight, so a rapid second click starts from the wrong card.
      animating = true;
      track.style.scrollSnapType = 'none';
      track.scrollTo({ left: offsetOf(list[i]), behavior: 'smooth' });

      window.clearTimeout(track._dglSnapTimer);
      track._dglSnapTimer = window.setTimeout(function () {
        track.style.scrollSnapType = '';
        animating = false;
        // Re-sync in case the browser landed a pixel or two off.
        index = nearestIndex();
        paint();
      }, 450);

      buildDots();
      paint();
    }

    track.addEventListener('scroll', function () {
      if (animating) return;
      window.clearTimeout(track._dglTimer);
      track._dglTimer = window.setTimeout(function () {
        if (animating) return;
        index = nearestIndex();
        buildDots();
        paint();
      }, 80);
    }, { passive: true });

    if (prev) prev.addEventListener('click', function () { goTo(index - perPage()); });
    if (next) next.addEventListener('click', function () { goTo(index + perPage()); });

    if (dotsWrap) {
      dotsWrap.addEventListener('click', function (e) {
        var dot = e.target.closest('[data-dgl-page]');
        if (!dot) return;
        goTo(Number(dot.dataset.dglPage) * perPage());
      });
    }

    // A real gesture always wins over an in-flight programmatic scroll.
    ['touchstart', 'wheel', 'pointerdown'].forEach(function (evt) {
      track.addEventListener(evt, function () {
        if (!animating) return;
        animating = false;
        window.clearTimeout(track._dglSnapTimer);
        track.style.scrollSnapType = '';
      }, { passive: true });
    });

    function measure() {
      if (!track.clientWidth) return;
      index = nearestIndex();
      buildDots();
      paint();
    }

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(measure).observe(track);
    } else {
      window.addEventListener('resize', measure);
    }
    measure();
  }

  function initKlaviyo(root) {
    if (root.dataset.dglKlReady === '1') return;
    root.dataset.dglKlReady = '1';
    root.addEventListener('click', function (e) {
      e.preventDefault();
      var id = root.dataset.dglKlaviyoForm;

      if (!id) {
        console.warn('[dip] No Klaviyo form ID set on this CTA.');
        return;
      }

      // Preferred: the initialised API object.
      if (window.klaviyo && typeof window.klaviyo.openForm === 'function') {
        window.klaviyo.openForm(id);
        return;
      }

      // Fallback: the pre-init queue, for clicks before Klaviyo has loaded.
      if (window._klOnsite && typeof window._klOnsite.push === 'function') {
        window._klOnsite.push(['openForm', id]);
        return;
      }

      console.warn('[dip] Klaviyo onsite script not found on this page.');
    });
  }

  function initModals(scope) {
    var ctx = scope || document;

    ctx.querySelectorAll('[data-dgl-modal-open]').forEach(function (trigger) {
      if (trigger.dataset.dglModalReady === '1') return;
      trigger.dataset.dglModalReady = '1';
      trigger.addEventListener('click', function () {
        var modal = document.getElementById(trigger.dataset.dglModalOpen);
        if (modal) openModal(modal, trigger);
      });
    });

    ctx.querySelectorAll('[data-dgl-modal]').forEach(function (modal) {
      if (modal.dataset.dglModalReady === '1') return;
      modal.dataset.dglModalReady = '1';
      if (!modal.classList.contains('is-open')) modal.setAttribute('inert', '');
      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-dgl-modal-close]')) closeModal(modal);
      });
    });
  }

  function openModal(modal, trigger) {
    modal._dglTrigger = trigger || null;
    modal.classList.add('is-open');
    modal.removeAttribute('inert');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('dgl-modal-open');

    // Focus the first field of the Klaviyo form if it has rendered.
    var target = modal.querySelector('input, button:not([data-dgl-modal-close]), [tabindex]')
      || modal.querySelector('[data-dgl-modal-close]');
    if (target) {
      window.setTimeout(function () { target.focus(); }, 50);
    }

    document.addEventListener('keydown', modal._dglKeyHandler = function (e) {
      if (e.key === 'Escape') closeModal(modal);
      if (e.key !== 'Tab') return;

      // Keep focus inside the panel.
      var focusables = modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    });
  }

  function closeModal(modal) {
    modal.classList.remove('is-open');
    modal.setAttribute('inert', '');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('dgl-modal-open');
    if (modal._dglKeyHandler) {
      document.removeEventListener('keydown', modal._dglKeyHandler);
      modal._dglKeyHandler = null;
    }
    if (modal._dglTrigger) modal._dglTrigger.focus();
  }

  function initVideos(scope) {
    var ctx = scope || document;
    var videos = ctx.querySelectorAll('[data-dgl-video]');
    if (!videos.length) return;

    var reduceMotion = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function mode(video) {
      var host = video.closest('[data-dgl-playback]');
      return host ? host.dataset.dglPlayback : 'inview';
    }

    function play(video) {
      video.muted = true; // property, not just the attribute - iOS requires it
      var p = video.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked, poster stays */ });
    }

    videos.forEach(function (video) {
      if (video.dataset.dglVideoReady === '1') return;
      video.dataset.dglVideoReady = '1';
      video.muted = true;

      var m = mode(video);

      if (m === 'tap') {
        // Bind on the card, not the video: the video is pointer-events:none so
        // that swipes starting on it still scroll the track.
        var card = video.closest('.dgl-ex-card') || video.parentElement;
        card.addEventListener('mouseenter', function () { play(video); });
        card.addEventListener('mouseleave', function () { video.pause(); });
        card.addEventListener('click', function () {
          if (video.paused) play(video); else video.pause();
        });
      }
    });

    if (typeof IntersectionObserver === 'undefined') return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        var m = mode(video);

        if (entry.isIntersecting) {
          // Start fetching only once the card is actually near the viewport.
          if (video.preload === 'none') {
            video.preload = 'auto';
            video.load();
          }
          if (m === 'inview' && !reduceMotion) play(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }, { threshold: 0.5, rootMargin: '100px' });

    videos.forEach(function (video) { io.observe(video); });
  }

  // ---------------------------------------------------------------
  // Newsletter state. Liquid cannot read query parameters and the page is
  // full-page cached, so the swap is done here. Both states are in the DOM;
  // one gets hidden. The flag is remembered for the session so it survives
  // navigation away from the tagged link.
  // ---------------------------------------------------------------
  function isSubscriber() {
    var root = document.querySelector('[data-dgl-nl-param]');
    var param = (root && root.dataset.dglNlParam) || 'nl';
    var wanted = (root && root.dataset.dglNlValue) || '';
    var key = 'dgl:subscriber';

    var value = null;
    try {
      value = new URLSearchParams(window.location.search).get(param);
    } catch (e) {
      var m = window.location.search.match(new RegExp('[?&]' + param + '=([^&]*)'));
      value = m ? decodeURIComponent(m[1]) : null;
    }

    if (value !== null && (wanted === '' || value === wanted)) {
      try { window.sessionStorage.setItem(key, '1'); } catch (e) {}
      return true;
    }

    try { return window.sessionStorage.getItem(key) === '1'; } catch (e) { return false; }
  }

  // The class goes on <html>, not on each CTA: the stylesheet hides one state
  // or the other, so the correct one is right at first paint. Toggling per
  // element after load would flash the default button for a frame.
  function applySubscriberState() {
    document.documentElement.classList.toggle('dgl-subscriber', isSubscriber());
  }

  function initAll(scope) {
    var ctx = scope || document;
    ctx.querySelectorAll('[data-dgl-carousel]').forEach(initCarousel);
    ctx.querySelectorAll('[data-dgl-klaviyo-form]').forEach(initKlaviyo);
    initModals(ctx);
    initVideos(ctx);
  }

  // Runs immediately - the script is deferred, so this still executes before
  // first paint of the CTA area in practice, and the CSS does the rest.
  applySubscriberState();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
